"use client"

import { useRef, useCallback, useState, useEffect, useLayoutEffect } from "react"
import { Check, Plus, X } from "lucide-react"
import type { StepProps, PhotoItem } from "@/types/listing"
import type { StorySlide } from "@/lib/editor-types"
import { getNormalizedImageFile, validateImageFile } from "@/lib/image-upload"
import { asRawImageRef, resolveStorageUrl, type RawImageRef } from "@/lib/storage/public-url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"

export function ArrangePhotosStep({
  data,
  updateDataAction,
  onPrevAction,
  onRegisterSave,
  onValidationChange,
  compact,
  fullViewport,
}: StepProps) {
  const [items, setItems] = useState<PhotoItem[]>(() =>
    Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0
      ? (data.editedPhotos as PhotoItem[])
      : Array.isArray(data.photos)
        ? (data.photos as PhotoItem[])
        : []
  )
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)
  const dragItemRef = useRef<number | null>(null)
  const touchCurrentIndex = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const rectsRef = useRef<Map<string, DOMRect>>(new Map())
  const [flipTick, setFlipTick] = useState(0)
  const [touchGhost, setTouchGhost] = useState<null | {
    /** Raw copy of `PhotoItem.preview`; resolved at the `<img src>` sink below. */
    preview: RawImageRef
    x: number
    y: number
    w: number
    h: number
    ox: number
    oy: number
  }>(null)
  const touchGhostRef = useRef(touchGhost)
  touchGhostRef.current = touchGhost
  const touchRafRef = useRef<number | null>(null)
  const lastSyncedIdsRef = useRef<string>("__init__")

  useEffect(() => {
    const incoming =
      Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0
        ? (data.editedPhotos as PhotoItem[])
        : Array.isArray(data.photos)
          ? (data.photos as PhotoItem[])
          : []
    const ids = incoming.map((p) => p.photoId).join("|")
    if (ids === lastSyncedIdsRef.current) return
    lastSyncedIdsRef.current = ids
    setItems(incoming)
  }, [data.photos, data.editedPhotos])

  useEffect(() => {
    onValidationChange?.(items.length > 0)
  }, [items.length, onValidationChange])

  const save = useCallback(() => {
    lastSyncedIdsRef.current = items.map((p) => p.photoId).join("|")
    const ids = items.map((p) => p.photoId)
    const editedMap = new Map(items.map((p) => [p.photoId, p]))
    const original = Array.isArray(data.photos) ? (data.photos as PhotoItem[]) : []
    const originalMap = new Map(original.map((p) => [p.photoId, p]))
    const nextOriginal = ids.map((id) => originalMap.get(id) ?? editedMap.get(id)).filter(Boolean) as PhotoItem[]

    let nextEditorState = data.photoEditorState
    if (nextEditorState?.slides?.length) {
      const activeId = nextEditorState.slides[nextEditorState.activeIndex]?.id
      const slideMap = new Map(nextEditorState.slides.map((s) => [s.id, s]))
      const nextSlides = ids
        .map((id) => slideMap.get(id))
        .filter((s): s is StorySlide => Boolean(s))
      const nextHistory: typeof nextEditorState.history = {}
      for (const id of ids) {
        const h = nextEditorState.history?.[id]
        if (h) nextHistory[id] = h
      }
      const nextActiveIndex = activeId ? ids.indexOf(activeId) : 0
      nextEditorState = {
        ...nextEditorState,
        slides: nextSlides,
        history: nextHistory,
        activeIndex: nextActiveIndex >= 0 ? nextActiveIndex : 0,
      }
    }

    updateDataAction({ editedPhotos: items, photos: nextOriginal, photoEditorState: nextEditorState })
  }, [items, updateDataAction, data.photos, data.editedPhotos, data.photoEditorState])

  useEffect(() => {
    onRegisterSave?.(save)
    return () => onRegisterSave?.(null)
  }, [onRegisterSave, save])

  const captureRects = useCallback(() => {
    if (!containerRef.current) return
    const map = new Map<string, DOMRect>()
    containerRef.current.querySelectorAll<HTMLElement>("[data-photo-id]").forEach((el) => {
      const id = el.getAttribute("data-photo-id")
      if (id) map.set(id, el.getBoundingClientRect())
    })
    rectsRef.current = map
    setFlipTick((t) => t + 1)
  }, [])

  useLayoutEffect(() => {
    if (!containerRef.current || rectsRef.current.size === 0) return
    const oldRects = rectsRef.current

    containerRef.current.querySelectorAll<HTMLElement>("[data-photo-id]").forEach((el) => {
      const id = el.getAttribute("data-photo-id")
      if (!id) return
      const oldRect = oldRects.get(id)
      if (!oldRect) return

      const newRect = el.getBoundingClientRect()
      const dx = oldRect.left - newRect.left
      const dy = oldRect.top - newRect.top
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return

      el.style.transition = "none"
      el.style.transform = `translate(${dx}px, ${dy}px)`

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transition = "transform 300ms cubic-bezier(0.2, 0, 0, 1)"
          el.style.transform = ""
          const cleanup = () => {
            el.style.transition = ""
            el.removeEventListener("transitionend", cleanup)
          }
          el.addEventListener("transitionend", cleanup)
        })
      })
    })

    rectsRef.current = new Map()
  }, [items, flipTick])

  const swapItems = useCallback(
    (from: number, to: number) => {
      if (from === to) return
      captureRects()
      setItems((prev) => {
        const next = [...prev]
        const temp = next[from]
        next[from] = next[to]
        next[to] = temp
        return next
      })
    },
    [captureRects]
  )

  const handleContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!containerRef.current) return
      if (dragItemRef.current === null) return
      const target = document
        .elementFromPoint(e.clientX, e.clientY)
        ?.closest?.("[data-photo-id]") as HTMLElement | null
      if (!target) return
      const id = target.getAttribute("data-photo-id")
      if (!id) return
      const idx = items.findIndex((p) => p.photoId === id)
      if (idx < 0) return
      setOverIndex(idx)
    },
    [items]
  )

  const handleContainerDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (dragItemRef.current === null || overIndex === null) return
      swapItems(dragItemRef.current, overIndex)
      dragItemRef.current = null
      setDragIndex(null)
      setOverIndex(null)
    },
    [overIndex, swapItems]
  )

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragItemRef.current = index
    setDragIndex(index)
    if (!e.dataTransfer) return
    const id = items[index]?.photoId
    if (id) {
      e.dataTransfer.effectAllowed = "move"
      e.dataTransfer.setData("text/plain", id)
    }
    const el = e.currentTarget as HTMLElement
    const clone = el.cloneNode(true) as HTMLElement
    clone.style.position = "absolute"
    clone.style.top = "-10000px"
    clone.style.left = "-10000px"
    clone.style.width = `${el.getBoundingClientRect().width}px`
    clone.style.height = `${el.getBoundingClientRect().height}px`
    clone.style.transform = "scale(1.02)"
    clone.style.opacity = "0.98"
    document.body.appendChild(clone)
    const rect = clone.getBoundingClientRect()
    e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2)
    requestAnimationFrame(() => {
      clone.remove()
    })
  }, [items])

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (dragItemRef.current === null) return
    setOverIndex(index)
  }, [])

  const handleDrop = useCallback(
    (index: number) => {
      if (dragItemRef.current === null) return
      swapItems(dragItemRef.current, index)
      dragItemRef.current = null
      setDragIndex(null)
      setOverIndex(null)
    },
    [swapItems]
  )

  const handleDragEnd = useCallback(() => {
    dragItemRef.current = null
    setDragIndex(null)
    setOverIndex(null)
  }, [])

  const handleTouchStart = useCallback((index: number, e: React.TouchEvent) => {
    touchCurrentIndex.current = index
    setDragIndex(index)
    e.currentTarget.closest("[data-reorder-container]")?.classList.add("overflow-hidden")
    const touch = e.touches[0]
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const p = items[index]
    if (p) {
      setTouchGhost({
        preview: p.preview,
        x: touch.clientX - (touch.clientX - rect.left),
        y: touch.clientY - (touch.clientY - rect.top),
        w: rect.width,
        h: rect.height,
        ox: touch.clientX - rect.left,
        oy: touch.clientY - rect.top,
      })
    }
  }, [items])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchCurrentIndex.current === null || !containerRef.current) return
    e.preventDefault()
    const touch = e.touches[0]
    const ghost = touchGhostRef.current
    if (ghost) {
      if (touchRafRef.current === null) {
        touchRafRef.current = requestAnimationFrame(() => {
          touchRafRef.current = null
          const g = touchGhostRef.current
          if (!g) return
          const t = e.touches[0]
          if (!t) return
          setTouchGhost((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              x: t.clientX - prev.ox,
              y: t.clientY - prev.oy,
            }
          })
        })
      }
    }
    const elements = containerRef.current.querySelectorAll<HTMLElement>("[data-photo-id]")
    let hoveredIndex: number | null = null
    elements.forEach((el, i) => {
      const rect = el.getBoundingClientRect()
      if (touch.clientX >= rect.left && touch.clientX <= rect.right && touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        hoveredIndex = i
      }
    })
    if (hoveredIndex !== null) setOverIndex(hoveredIndex)
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (touchCurrentIndex.current !== null && overIndex !== null) {
      swapItems(touchCurrentIndex.current, overIndex)
    }
    touchCurrentIndex.current = null
    setDragIndex(null)
    setOverIndex(null)
    containerRef.current?.closest("[data-reorder-container]")?.classList.remove("overflow-hidden")
    setTouchGhost(null)
    if (touchRafRef.current !== null) {
      cancelAnimationFrame(touchRafRef.current)
      touchRafRef.current = null
    }
  }, [overIndex, swapItems])

  const addInputRef = useRef<HTMLInputElement>(null)

  const generateId = useCallback(() => Math.random().toString(36).slice(2, 10), [])

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return
      const selected = Array.from(files).filter((f) => validateImageFile(f).valid)
      if (selected.length === 0) return
      const newItems = await Promise.all(
        selected.map(async (file) => {
          const { file: normalized, previewUrl } = await getNormalizedImageFile(file)
          return {
            file: normalized,
            // Fresh client-only `blob:` preview — raw until uploaded.
            preview: asRawImageRef(previewUrl),
            photoId: generateId(),
          } satisfies PhotoItem
        })
      )
      setItems((prev) => [...prev, ...newItems])
    },
    [generateId]
  )

  const removeAt = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const rootClass = fullViewport
    ? "flex h-dvh flex-col bg-background"
    : "space-y-4"

  return (
    <div data-reorder-container className={rootClass}>
      <input
        ref={addInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {fullViewport && (
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={onPrevAction}
            className="flex size-10 items-center justify-center rounded-full transition-opacity active:opacity-60"
            aria-label="Back"
          >
            <X className="size-5 text-foreground" strokeWidth={1.5} />
          </button>
          <span className="text-sm font-medium text-foreground">Reorder Photos</span>
          <button
            onClick={save}
            className="flex size-10 items-center justify-center rounded-full transition-opacity active:opacity-60"
            aria-label="Save"
          >
            <Check className="size-5 text-foreground" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {!fullViewport && (
        <p className="text-sm text-muted-foreground">Drag to reorder. Tap a photo to remove.</p>
      )}

      <div
        className={fullViewport ? "flex-1 overflow-y-auto px-4 pb-6" : "px-0"}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
          <div
            ref={containerRef}
            className={fullViewport ? "mx-auto grid max-w-md grid-cols-3 gap-2.5" : "grid grid-cols-3 md:grid-cols-4 gap-2.5"}
            onDragOver={handleContainerDragOver}
            onDrop={handleContainerDrop}
          >
          {items.map((p, i) => (
            <div
              key={p.photoId}
              data-photo-id={p.photoId}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={() => handleDrop(i)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(i, e)}
              className={`relative cursor-grab overflow-hidden rounded-xl border-2 will-change-transform active:cursor-grabbing ${
                dragIndex === i
                  ? "scale-[0.98] border-foreground/40 opacity-30"
                  : overIndex === i
                    ? "border-foreground ring-2 ring-foreground/20"
                    : "border-foreground/15"
              }`}
                    style={{ aspectRatio: "3 / 4" }}
            >
              <img
                // 3:4 reorder tile. `p.preview` is a `blob:` URL for a freshly
                // picked File and a bucket path when a draft is re-opened —
                // `imagePresetUrl` passes `blob:` through untouched, so one
                // call covers both and the second case stops downloading the
                // full upload into a ~110 px cell. Same rung as the review
                // steps downstream, so the tile is already cached there.
                src={imagePresetUrl(resolveStorageUrl(p.preview), CARD_SM)}
                alt=""
                className="pointer-events-none size-full object-cover"
                draggable={false}
              />
              {/* Over-image chrome: painted on the photo, so it keeps its literal
                  dark scrim + white ink in both themes (cf. photos-upload-step). */}
              <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-2">
                <span className="flex size-6 items-center justify-center rounded-full bg-neutral-900/80 text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="size-6" />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  removeAt(i)
                }}
                className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-neutral-900/70 text-white backdrop-blur-sm transition-opacity active:opacity-70"
                aria-label="Remove photo"
              >
                <X className="size-4" strokeWidth={2.5} />
              </button>
            </div>
          ))}

          <div
            className="relative flex items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-foreground/15"
                  style={{ aspectRatio: "3 / 4" }}
          >
            <button
              type="button"
              onClick={() => addInputRef.current?.click()}
              className="flex flex-col items-center gap-2 text-muted-foreground/70 transition-opacity active:opacity-60"
              aria-label="Add more photos"
            >
              <div className="flex size-12 items-center justify-center rounded-full border-2 border-dashed border-foreground/15">
                <Plus className="size-6" strokeWidth={1.5} />
              </div>
              <span className="text-xs font-medium">Add</span>
            </button>
          </div>
        </div>
      </div>

      {touchGhost && (
        <div
          className="pointer-events-none fixed left-0 top-0 z-9999 rounded-xl shadow-2xl"
          style={{
            width: touchGhost.w,
            height: touchGhost.h,
            transform: `translate3d(${touchGhost.x}px, ${touchGhost.y}px, 0) scale(1.03)`,
          }}
        >
          <img
            // Same tile at the same size — same preset, so the ghost repaints
            // from the browser cache instead of refetching.
            src={imagePresetUrl(resolveStorageUrl(touchGhost.preview), CARD_SM)}
            alt=""
            className="size-full rounded-xl object-cover"
            draggable={false}
          />
        </div>
      )}
    </div>
  )
}
